import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiCopy, FiRefreshCw, FiCheck, FiSettings } from 'react-icons/fi';
import './PasswordGeneratorDemo.css';

const PasswordGeneratorDemo = () => {
  const [password, setPassword] = useState('');
  const [length, setLength] = useState(12);
  const [options, setOptions] = useState({
    uppercase: true,
    numbers: true,
    symbols: true
  });
  const [copied, setCopied] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  // Funções para gerar caracteres aleatórios
  const getRandomLower = () => String.fromCharCode(Math.floor(Math.random() * 26) + 97);
  const getRandomUpper = () => String.fromCharCode(Math.floor(Math.random() * 26) + 65);
  const getRandomNumber = () => String.fromCharCode(Math.floor(Math.random() * 10) + 48);
  const getRandomSymbol = () => {
    const symbols = '!@#$%^&*(){}[]=<>/,.';
    return symbols[Math.floor(Math.random() * symbols.length)];
  };

  const randomFunc = {
    lower: getRandomLower,
    upper: getRandomUpper,
    number: getRandomNumber,
    symbol: getRandomSymbol
  };

  const generatePasswordLogic = useCallback((upper, number, symbol, length) => {
    let generatedPassword = '';
    const typesCount = upper + number + symbol + 1; // +1 para lowercase sempre incluído
    const typesArr = [
      { lower: true }, 
      { upper }, 
      { number }, 
      { symbol }
    ].filter(item => Object.values(item)[0]);

    if (typesCount === 0) {
      return '';
    }

    for (let i = 0; i < length; i++) {
      typesArr.forEach(type => {
        const funcName = Object.keys(type)[0];
        generatedPassword += randomFunc[funcName]();
      });
    }

    return generatedPassword.slice(0, length);
  }, []);

  const generatePassword = useCallback(() => {
    setIsGenerating(true);
    
    // Validar limites
    const validLength = Math.max(1, Math.min(30, length));
    if (validLength !== length) {
      setLength(validLength);
    }

    // Simular um pequeno delay para animação
    setTimeout(() => {
      const newPassword = generatePasswordLogic(
        options.uppercase,
        options.numbers,
        options.symbols,
        validLength
      );
      setPassword(newPassword);
      setIsGenerating(false);
      setCopied(false);
    }, 300);
  }, [length, options, generatePasswordLogic]);

  const copyPassword = async () => {
    if (!password) return;

    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy password:', err);
      // Fallback para navegadores mais antigos
      const textArea = document.createElement('textarea');
      textArea.value = password;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleOptionChange = (option) => {
    setOptions(prev => ({
      ...prev,
      [option]: !prev[option]
    }));
  };

  const handleLengthChange = (e) => {
    const value = parseInt(e.target.value) || 1;
    setLength(Math.max(1, Math.min(30, value)));
  };

  // Calcular força da senha
  const getPasswordStrength = () => {
    if (!password) return { level: 0, text: 'Nenhuma' };
    
    let score = 0;
    if (password.length >= 8) score += 1;
    if (password.length >= 12) score += 1;
    if (/[a-z]/.test(password)) score += 1;
    if (/[A-Z]/.test(password)) score += 1;
    if (/[0-9]/.test(password)) score += 1;
    if (/[^A-Za-z0-9]/.test(password)) score += 1;

    if (score <= 2) return { level: 1, text: 'Fraca', color: '#ff4757' };
    if (score <= 4) return { level: 2, text: 'Média', color: '#ffa502' };
    return { level: 3, text: 'Forte', color: '#2ed573' };
  };

  const strength = getPasswordStrength();

  return (
    <div className="password-generator-demo">
      <div className="demo-header">
        <div className="demo-title">
          <FiSettings className="demo-icon" />
          <h3>Gerador de Senhas</h3>
        </div>
        <p className="demo-description">
          Gere senhas seguras e personalizáveis com diferentes opções de caracteres
        </p>
      </div>

      <div className="generator-container">
        {/* Campo de senha gerada */}
        <div className="password-output">
          <div className="password-field">
            <input
              type="text"
              value={password}
              readOnly
              placeholder="Sua senha aparecerá aqui..."
              className="password-input"
            />
            <motion.button
              className={`copy-btn ${copied ? 'copied' : ''}`}
              onClick={copyPassword}
              disabled={!password}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <AnimatePresence mode="wait">
                {copied ? (
                  <motion.div
                    key="check"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0 }}
                  >
                    <FiCheck />
                  </motion.div>
                ) : (
                  <motion.div
                    key="copy"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0 }}
                  >
                    <FiCopy />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.button>
          </div>
          
          {/* Indicador de força da senha */}
          {password && (
            <motion.div
              className="password-strength"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="strength-label">Força da senha:</div>
              <div className="strength-indicator">
                <div 
                  className="strength-bar"
                  style={{ 
                    width: `${(strength.level / 3) * 100}%`,
                    backgroundColor: strength.color 
                  }}
                />
              </div>
              <div 
                className="strength-text"
                style={{ color: strength.color }}
              >
                {strength.text}
              </div>
            </motion.div>
          )}
        </div>

        {/* Configurações */}
        <div className="generator-settings">
          {/* Comprimento da senha */}
          <div className="setting-group">
            <label className="setting-label">
              Comprimento da senha: <span className="length-value">{length}</span>
            </label>
            <div className="length-control">
              <input
                type="range"
                min="1"
                max="30"
                value={length}
                onChange={handleLengthChange}
                className="length-slider"
              />
              <input
                type="number"
                min="1"
                max="30"
                value={length}
                onChange={handleLengthChange}
                className="length-input"
              />
            </div>
          </div>

          {/* Opções de caracteres */}
          <div className="setting-group">
            <label className="setting-label">Incluir caracteres:</label>
            <div className="options-grid">
              <label className="option-item">
                <input
                  type="checkbox"
                  checked={true}
                  disabled
                  className="option-checkbox"
                />
                <span className="option-text">Minúsculas (a-z)</span>
                <span className="option-required">Obrigatório</span>
              </label>

              <label className="option-item">
                <input
                  type="checkbox"
                  checked={options.uppercase}
                  onChange={() => handleOptionChange('uppercase')}
                  className="option-checkbox"
                />
                <span className="option-text">Maiúsculas (A-Z)</span>
              </label>

              <label className="option-item">
                <input
                  type="checkbox"
                  checked={options.numbers}
                  onChange={() => handleOptionChange('numbers')}
                  className="option-checkbox"
                />
                <span className="option-text">Números (0-9)</span>
              </label>

              <label className="option-item">
                <input
                  type="checkbox"
                  checked={options.symbols}
                  onChange={() => handleOptionChange('symbols')}
                  className="option-checkbox"
                />
                <span className="option-text">Símbolos (!@#$%^&*)</span>
              </label>
            </div>
          </div>
        </div>

        {/* Botão gerar */}
        <motion.button
          className="generate-btn"
          onClick={generatePassword}
          disabled={isGenerating}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <motion.div
            animate={isGenerating ? { rotate: 360 } : { rotate: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            <FiRefreshCw />
          </motion.div>
          {isGenerating ? 'Gerando...' : 'Gerar Senha'}
        </motion.button>
      </div>

      {/* Notificação de cópia */}
      <AnimatePresence>
        {copied && (
          <motion.div
            className="copy-notification"
            initial={{ opacity: 0, y: 50, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -50, scale: 0.8 }}
            transition={{ duration: 0.3 }}
          >
            <FiCheck />
            Senha copiada para a área de transferência!
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PasswordGeneratorDemo;